import { useState, useEffect, useCallback, useRef } from 'react';
import { ModelResponse, ModelMetrics, WebSocketMessage } from '../types';
import { ConnectionState, webSocketManager } from '../services/WebSocketManager';

export interface UseStreamingResponsesOptions {
  demoRunId: string | null;
  isRunning: boolean;
  expectedModelIds?: string[];
}

export interface UseStreamingResponsesResult {
  responses: Record<string, ModelResponse>;
  allComplete: boolean;
  queuePosition: number | null;
  connectionState: ConnectionState;
}

const TERMINAL_STATUSES: ModelResponse['status'][] = ['complete', 'error', 'timeout'];

function isTerminal(status: ModelResponse['status']): boolean {
  return TERMINAL_STATUSES.includes(status);
}

function computeAllComplete(responses: Record<string, ModelResponse>): boolean {
  const entries = Object.values(responses);
  if (entries.length === 0) return false;
  return entries.every(r => isTerminal(r.status));
}

function createModelResponse(modelId: string): ModelResponse {
  return {
    modelId,
    modelName: modelId,
    provider: '',
    responseText: '',
    status: 'streaming',
    isGrok: modelId.toLowerCase().includes('grok'),
  };
}

export function useStreamingResponses(
  options: UseStreamingResponsesOptions
): UseStreamingResponsesResult {
  const { demoRunId, isRunning, expectedModelIds } = options;

  const [responses, setResponses] = useState<Record<string, ModelResponse>>({});
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    webSocketManager.getConnectionState()
  );

  // Keep a ref to demoRunId so the message handler always sees the latest value
  // without needing to re-create the callback (which causes unsubscribe/resubscribe gaps)
  const demoRunIdRef = useRef<string | null>(demoRunId);
  useEffect(() => {
    demoRunIdRef.current = demoRunId;
  }, [demoRunId]);

  // Reset state when demoRunId changes; pre-populate with expected models
  const prevDemoRunIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (demoRunId !== prevDemoRunIdRef.current) {
      prevDemoRunIdRef.current = demoRunId;
      if (expectedModelIds && expectedModelIds.length > 0) {
        const initial: Record<string, ModelResponse> = {};
        for (const modelId of expectedModelIds) {
          initial[modelId] = createModelResponse(modelId);
        }
        setResponses(initial);
      } else {
        setResponses({});
      }
      setQueuePosition(null);
    }
  }, [demoRunId, expectedModelIds]);

  // Handle incoming WebSocket messages — uses ref so it never goes stale
  const handleMessage = useCallback(
    (message: WebSocketMessage) => {
      const currentDemoRunId = demoRunIdRef.current;
      // Filter by demoRunId
      if (!currentDemoRunId || message.demoRunId !== currentDemoRunId) return;

      switch (message.type) {
        case 'token': {
          const { modelId, text } = message.payload;
          setResponses(prev => {
            const existing = prev[modelId] || createModelResponse(modelId);
            // Don't append tokens to models already in terminal state
            if (isTerminal(existing.status)) return prev;
            return {
              ...prev,
              [modelId]: {
                ...existing,
                responseText: existing.responseText + text,
                status: 'streaming',
              },
            };
          });
          break;
        }

        case 'complete': {
          const { modelId, totalTokens, latencyMs } = message.payload;
          setResponses(prev => {
            const existing = prev[modelId] || createModelResponse(modelId);
            const metrics: ModelMetrics = {
              ...existing.metrics,
              totalTokens,
              responseTime: latencyMs,
            };
            return {
              ...prev,
              [modelId]: {
                ...existing,
                status: 'complete',
                metrics,
              },
            };
          });
          break;
        }

        case 'error': {
          const { modelId, errorCode, message: errorMsg } = message.payload;
          setResponses(prev => {
            const existing = prev[modelId] || createModelResponse(modelId);
            const status: ModelResponse['status'] =
              errorCode.toLowerCase().includes('timeout') ? 'timeout' : 'error';
            return {
              ...prev,
              [modelId]: {
                ...existing,
                status,
                errorMessage: errorMsg,
                // Preserve partial responseText
              },
            };
          });
          break;
        }

        case 'metrics': {
          const { modelId, metrics } = message.payload;
          setResponses(prev => {
            const existing = prev[modelId] || createModelResponse(modelId);
            return {
              ...prev,
              [modelId]: {
                ...existing,
                metrics: { ...existing.metrics, ...metrics },
              },
            };
          });
          break;
        }

        case 'queue_update': {
          setQueuePosition(message.payload.queuePosition);
          break;
        }

        case 'status': {
          const { modelId, status: statusText } = message.payload;
          setResponses(prev => {
            const existing = prev[modelId] || createModelResponse(modelId);
            if (isTerminal(existing.status)) return prev;
            return {
              ...prev,
              [modelId]: {
                ...existing,
                statusText,
              },
            };
          });
          break;
        }
      }
    },
    [] // no deps — reads demoRunId from ref
  );

  // Subscribe to WebSocket messages while running
  // Only depends on isRunning — handleMessage is stable (no deps), demoRunId is read from ref
  useEffect(() => {
    if (!isRunning) return;
    const unsubscribe = webSocketManager.onMessage(handleMessage);
    return unsubscribe;
  }, [isRunning, handleMessage]);

  // Subscribe to connection state changes
  useEffect(() => {
    const unsubscribe = webSocketManager.onConnectionState(setConnectionState);
    return unsubscribe;
  }, []);

  const allComplete = computeAllComplete(responses);

  return { responses, allComplete, queuePosition, connectionState };
}
